/**
 * ChangePasswordForm.tsx — authenticated change-password form (Phase 2.3, T4)
 *
 * Hosted inside the Settings dialog. Kept as its own component so the logic
 * (validation, Cognito call, error/success copy) is unit-testable directly —
 * the Radix Dialog wrapper portals its content out of the tree, which jsdom
 * cannot see.
 *
 * Uses the shared passwordPolicy validator + error mapper so the copy matches
 * the reset and first-login flows. Client validation is UX-only; Cognito
 * re-enforces the policy and is the source of truth.
 */
import { useState } from 'react'
import type { FormEvent, ChangeEvent } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { changePassword } from '../auth/authService'
import { validatePassword, formatAuthError } from '../auth/passwordPolicy'

interface ChangePasswordFormProps {
  /** Called after a successful change (e.g. to close the dialog / toast). */
  onSuccess?: () => void
}

export function ChangePasswordForm({ onSuccess }: ChangePasswordFormProps) {
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setError(null)
    setSuccess(null)

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match.')
      return
    }
    const check = validatePassword(newPassword)
    if (!check.valid) {
      setError(check.message ?? 'Password does not meet the requirements.')
      return
    }

    setLoading(true)
    try {
      await changePassword(currentPassword, newPassword)
      setSuccess('Your password has been changed.')
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      onSuccess?.()
    } catch (err) {
      setError(formatAuthError(err, 'changePassword'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <form onSubmit={(e) => void handleSubmit(e)} noValidate>
      {error && (
        <div
          role="alert"
          className="mb-4 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
        >
          {error}
        </div>
      )}
      {success && (
        <div
          role="status"
          className="mb-4 rounded-lg border border-primary/30 bg-primary/10 px-3 py-2 text-sm text-primary"
        >
          {success}
        </div>
      )}

      <div className="mb-4">
        <Label htmlFor="current-password">Current password</Label>
        <Input
          id="current-password"
          type="password"
          autoComplete="current-password"
          value={currentPassword}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setCurrentPassword(e.target.value)}
          required
        />
      </div>

      <div className="mb-4">
        <Label htmlFor="new-password">New password</Label>
        <Input
          id="new-password"
          type="password"
          autoComplete="new-password"
          placeholder="8+ chars, upper, lower, number, symbol"
          value={newPassword}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setNewPassword(e.target.value)}
          required
        />
      </div>

      <div className="mb-4">
        <Label htmlFor="confirm-new-password">Confirm new password</Label>
        <Input
          id="confirm-new-password"
          type="password"
          autoComplete="new-password"
          value={confirmPassword}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setConfirmPassword(e.target.value)}
          required
        />
      </div>

      <Button type="submit" className="w-full" disabled={loading}>
        {loading ? 'Updating…' : 'Update password'}
      </Button>
    </form>
  )
}
